import React, { useCallback, useState } from "react";
import { useLeadBus } from "../../hooks/useLeadBus";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetCustomerQuery,
  useGetCustomerCrmSummaryQuery,
  useGetCustomerCrmLeadsQuery,
  useGetCustomerCrmQuotesQuery,
  useGetCustomerCrmInvoicesQuery,
  useGetCustomerCrmTimelineQuery,
  useGetCustomerNotesQuery,
  useCreateCustomerNoteMutation,
  useGetCustomerEmailHistoryQuery,
} from "../../Api/attoDeskApi";
import { ICustomer, ICrmLead, ICrmQuote, ICrmInvoice, ICrmTimelineEvent, ICustomerEmailHistoryRow } from "../../Api/Interface/api.interface";
import NewPopUpCustomer from "../Customer/Edit/index";
import CreateLeadDialog from "../LeadSystem/CreateLeadDialog";
import { formatLeadBudget } from "../LeadSystem/leadCellUtils";
import { ContactActionButtons } from "../../Components/ContactActions";

// MUI Icons
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import UpdateIcon from "@mui/icons-material/Update";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EventIcon from "@mui/icons-material/Event";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotesIcon from "@mui/icons-material/Notes";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { API_URL } from "../../config";

/* ── Helpers ────────────────────────────────────────────────────────── */

const generateColor = (name: string): string => {
  const colors = ["#1976d2","#388e3c","#f57c00","#7b1fa2","#c62828","#00796b","#0288d1","#5d4037"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const formatCurrency = (v: number | null | undefined) =>
  v != null ? `$${Number(v).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

const formatDate = (d: string | null | undefined) => {
  if (!d) return "—";
  // Strip any time portion — handles both "T"-separated ISO and the API's
  // space-separated "Y-m-d H:i" — then parse at local midnight to avoid the
  // timezone day-shift that turned a stored "2026-05-16" into "May 15".
  const dateOnly = String(d).split(/[T ]/)[0];
  const dt = new Date(dateOnly + "T00:00:00");
  return isNaN(dt.getTime())
    ? "—"
    : dt.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
};

const formatDateTime = (d: string | null | undefined) => {
  if (!d) return "Not available";
  // Accept both ISO ("…T…") and the API's space-separated "Y-m-d H:i".
  const dt = new Date(String(d).replace(" ", "T"));
  if (isNaN(dt.getTime())) return "Not available";
  return (
    dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) +
    " • " +
    dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
};

/* Lead status */
const LEAD_STATUS_MAP: Record<string, { label: string; color: "default"|"primary"|"secondary"|"error"|"info"|"success"|"warning" }> = {
  new:               { label: "New",              color: "info" },
  contacted:         { label: "Contacted",        color: "secondary" },
  negotiation:       { label: "Negotiation",      color: "warning" },
  ready_for_quote:   { label: "Ready for Quote",  color: "primary" },
  ready_for_booking: { label: "Ready to Book",    color: "success" },
  confirmed:         { label: "Confirmed",        color: "success" },
  lost:              { label: "Lost",             color: "error" },
};

const leadStatusBadge = (status: string) => {
  const s = LEAD_STATUS_MAP[status] ?? { label: status, color: "default" as const };
  return <Chip label={s.label} color={s.color} size="small" />;
};

/* AI Score */
const aiScoreBadge = (score: string | null) => {
  if (!score) return <Chip label="—" size="small" />;
  const lower = score.toLowerCase();
  if (lower === "hot")  return <Chip label="Hot"  icon={<LocalFireDepartmentIcon style={{ fontSize: 14 }} />} size="small" sx={{ bgcolor: "#ff5252", color: "#fff" }} />;
  if (lower === "warm") return <Chip label="Warm" icon={<WhatshotIcon style={{ fontSize: 14 }} />} size="small" sx={{ bgcolor: "#ff9800", color: "#fff" }} />;
  return <Chip label="Cold" icon={<AcUnitIcon style={{ fontSize: 14 }} />} size="small" sx={{ bgcolor: "#42a5f5", color: "#fff" }} />;
};

/* Quote status */
const QUOTE_STATUS_MAP: Record<string, { label: string; color: "default"|"primary"|"secondary"|"error"|"info"|"success"|"warning" }> = {
  draft:    { label: "Draft",    color: "default" },
  sent:     { label: "Sent",     color: "info" },
  accepted: { label: "Accepted", color: "success" },
  rejected: { label: "Rejected", color: "error" },
};

const quoteStatusBadge = (status: string) => {
  const s = QUOTE_STATUS_MAP[status] ?? { label: status, color: "default" as const };
  return <Chip label={s.label} color={s.color} size="small" />;
};

/* Invoice payment status */
const invoicePaymentBadge = (inv: ICrmInvoice) => {
  const paid    = Number(inv.paid_amount ?? 0);
  const amount  = Number(inv.amount ?? 0);
  const due     = Number(inv.due_amount ?? 0);
  if (amount > 0 && due <= 0) return <Chip label="Paid"    color="success" size="small" />;
  if (paid > 0 && due > 0)    return <Chip label="Partial" color="warning" size="small" />;
  return <Chip label="Unpaid" color="error" size="small" />;
};

/* Timeline icon */
const TimelineIcon = ({ type }: { type: string }) => {
  const style = { fontSize: 18 };
  if (type === "lead_created")   return <PersonAddAlt1Icon style={{ ...style, color: "#1976d2" }} />;
  if (type === "quote_created")  return <DescriptionIcon   style={{ ...style, color: "#7b1fa2" }} />;
  if (type === "invoice_created")return <ReceiptIcon       style={{ ...style, color: "#388e3c" }} />;
  if (type.includes("status"))   return <UpdateIcon        style={{ ...style, color: "#f57c00" }} />;
  if (type === "note")           return <NoteAddIcon       style={{ ...style, color: "#0288d1" }} />;
  if (type === "email")          return <EmailIcon         style={{ ...style, color: "#9c27b0" }} />;
  return <ShowChartIcon style={{ ...style, color: "#9e9e9e" }} />;
};

/* ── KPI Card ───────────────────────────────────────────────────────── */
interface KpiCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  sub?: string;
}
const KpiCard = ({ label, value, icon, color, sub }: KpiCardProps) => (
  <Card sx={{ flex: 1, minWidth: 120, border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: 2 }}>
    <CardContent sx={{ p: "14px !important" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: "#666", fontWeight: 500, lineHeight: 1.2 }}>{label}</Typography>
        <Box sx={{ bgcolor: `${color}18`, borderRadius: 1.5, p: 0.7, display: "flex" }}>{icon}</Box>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color, lineHeight: 1 }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: "#999" }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

/* ── Section Header ─────────────────────────────────────────────────── */
const SectionHeader = ({ title, action }: { title: string; action?: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{title}</Typography>
    {action}
  </Box>
);

/* ── Insight Card ───────────────────────────────────────────────────── */
const InsightCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card sx={{ mb: 2, border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: 2 }}>
    <CardContent sx={{ p: "16px !important" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "#333" }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const InsightRow = ({ label, value }: { label: string; value: string | number }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
    <Typography variant="body2" sx={{ color: "#666" }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>{value}</Typography>
  </Box>
);

/* ── Preference / Notes row — icon + stacked label & value ──────────── */
const PreferenceRow = ({
  icon,
  iconColor,
  label,
  value,
}: {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  value?: string | null;
}) => {
  const provided = Boolean(value && String(value).trim());
  return (
    <Box sx={{ display: "flex", gap: 1.2 }}>
      <Box
        sx={{
          bgcolor: `${iconColor}18`,
          borderRadius: 1.5,
          p: 0.7,
          height: "fit-content",
          display: "flex",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: "#999",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.3,
            display: "block",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: provided ? "#333" : "#bbb",
            fontStyle: provided ? "normal" : "italic",
            mt: 0.3,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}
        >
          {provided ? value : "Not provided"}
        </Typography>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                      */
/* ═══════════════════════════════════════════════════════════════════ */

const CustomerCRMProfile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: customerId } = useParams<{ id: string }>();

  const [openEditCustomer, setOpenEditCustomer] = useState(false);
  const [openLeadDialog, setOpenLeadDialog] = useState(false);

  /* ── Add Note dialog (Client Profile top action) ────────────────────
   * Opens on the "Add Note" button. Captures an internal customer-
   * level note via POST /customers/:id/notes. On success: closes the
   * modal, refreshes the notes list (RTK tag invalidation) and shows
   * a toast. On failure: keeps the modal open with the typed body
   * intact + shows an error toast so the rep can retry. */
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [createNote, { isLoading: noteSaving }] = useCreateCustomerNoteMutation();

  /* ── Email History "View Message" modal ──────────────────────────── */
  const [viewEmail, setViewEmail] = useState<ICustomerEmailHistoryRow | null>(null);

  /* ── Snackbar ────────────────────────────────────────────────────── */
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>(
    { open: false, msg: "", severity: "success" }
  );

  /* ── Data fetching ─────────────────────────────────────────────── */
  const { data: customerData, isLoading: customerLoading } = useGetCustomerQuery({
    searchText: "",
    id: String(customerId),
  });

  const { data: summaryData, isLoading: summaryLoading, refetch: refetchSummary } = useGetCustomerCrmSummaryQuery(
    { customerId: String(customerId) },
    { skip: !customerId }
  );
  const { data: leadsData,   isLoading: leadsLoading, refetch: refetchLeads }   = useGetCustomerCrmLeadsQuery(
    { customerId: String(customerId) },
    { skip: !customerId }
  );
  const { data: quotesData,  isLoading: quotesLoading }  = useGetCustomerCrmQuotesQuery(
    { customerId: String(customerId) },
    { skip: !customerId }
  );
  const { data: invoicesData, isLoading: invoicesLoading } = useGetCustomerCrmInvoicesQuery(
    { customerId: String(customerId) },
    { skip: !customerId }
  );
  const { data: timelineData, isLoading: timelineLoading, refetch: refetchTimeline } = useGetCustomerCrmTimelineQuery(
    { customerId: String(customerId) },
    { skip: !customerId }
  );
  /* Client Profile right-rail data — notes + sent email history. Both
     are tagged so the mutations / Compose modal onSent can invalidate
     them and trigger a re-fetch without manual refetch wiring. */
  const { data: notesData } = useGetCustomerNotesQuery(
    { customerId: String(customerId) },
    { skip: !customerId }
  );
  const { data: emailHistoryData, isLoading: emailHistoryLoading, refetch: refetchEmailHistory } =
    useGetCustomerEmailHistoryQuery(
      { customerId: String(customerId) },
      { skip: !customerId }
    );

  /** Save handler for the Add Note dialog. Triggers RTK invalidation
   *  on success so the notes list re-fetches automatically. */
  const handleSaveNote = async () => {
    if (!customerId || !noteText.trim()) return;
    try {
      await createNote({ customerId: String(customerId), note: noteText.trim() }).unwrap();
      setNoteText("");
      setNoteOpen(false);
      setSnack({ open: true, msg: "Note saved", severity: "success" });
    } catch (e: any) {
      const msg = e?.data?.message || e?.error || "Couldn't save the note. Try again.";
      setSnack({ open: true, msg, severity: "error" });
    }
  };

  /* ── Cross-surface refresh ───────────────────────────────────────
     RTK Query caches the CRM-leads / timeline / summary feeds. When a
     lead is created (here or from any other surface), updated, or
     reassigned anywhere in the app, the leadBus fires — we refetch the
     three queries that affect this page so the operator doesn't have
     to hit refresh. Same pattern the leads-list page uses. */
  const refreshCustomerLeadFeeds = useCallback(() => {
    refetchLeads();
    refetchTimeline();
    refetchSummary();
  }, [refetchLeads, refetchTimeline, refetchSummary]);

  useLeadBus(refreshCustomerLeadFeeds);

  /* ── Derived data ──────────────────────────────────────────────── */
  const customer: ICustomer | null = (customerData?.data as ICustomer[] | undefined)?.[0] ?? null;
  const summary  = summaryData?.data;
  const leads:    ICrmLead[]          = leadsData?.data    ?? [];
  const quotes:   ICrmQuote[]         = quotesData?.data   ?? [];
  const invoices: ICrmInvoice[]       = invoicesData?.data ?? [];
  const timeline: ICrmTimelineEvent[] = timelineData?.data ?? [];

  const fullName = customer
    ? `${customer.customerFirstName ?? ""} ${customer.customerLastName ?? ""}`.trim()
    : "—";

  const activeLead = leads.find((l) =>
    ["new","contacted","negotiation","ready_for_quote","ready_for_booking"].includes(l.status)
  ) ?? null;

  /* Lead insights */
  const eventTypeCounts = leads.reduce<Record<string, number>>((acc, l) => {
    if (l.event_type) acc[l.event_type] = (acc[l.event_type] ?? 0) + 1;
    return acc;
  }, {});
  const topEventType = Object.entries(eventTypeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";
  const avgGuests    = leads.length
    ? Math.round(leads.reduce((s, l) => s + (l.guest_count ?? 0), 0) / leads.length)
    : 0;
  const avgBudget    = leads.filter((l) => l.budget_value).length
    ? leads.reduce((s, l) => s + (l.budget_value ?? 0), 0) / leads.filter((l) => l.budget_value).length
    : 0;
  const hallCounts   = leads.reduce<Record<string, number>>((acc, l) => {
    if (l.preferred_hall) acc[l.preferred_hall] = (acc[l.preferred_hall] ?? 0) + 1;
    return acc;
  }, {});
  const topHall = Object.entries(hallCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

  /* ── Loading state ─────────────────────────────────────────────── */
  if (customerLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <Box sx={{ bgcolor: "#f5f6fa", minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>

      {/* ── PROFILE HEADER ─────────────────────────────────────────── */}
      <Card sx={{ mb: 2.5, border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: 2 }}>
        <CardContent sx={{ p: "20px !important" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5, flexWrap: "wrap" }}>
            {/* Avatar */}
            <Avatar
              src={customer?.customerImg || undefined}
              sx={{ width: 72, height: 72, bgcolor: generateColor(fullName), fontSize: "1.6rem", fontWeight: 700, flexShrink: 0 }}
            >
              {fullName.charAt(0).toUpperCase()}
            </Avatar>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a2e", lineHeight: 1.2 }}>{fullName}</Typography>
              {customer?.customerCompanyName && (
                <Typography variant="body2" sx={{ color: "#666", mt: 0.3 }}>{customer.customerCompanyName}</Typography>
              )}
              <Box sx={{ display: "flex", gap: 2, mt: 0.8, flexWrap: "wrap" }}>
                {customer?.customerMobileNum && (
                  <Typography variant="body2" sx={{ color: "#555", display: "flex", alignItems: "center", gap: 0.4 }}>
                    <PhoneIcon sx={{ fontSize: 14 }} />{customer.customerMobileNum}
                  </Typography>
                )}
                {customer?.customerEmail && (
                  <Typography variant="body2" sx={{ color: "#555", display: "flex", alignItems: "center", gap: 0.4 }}>
                    <EmailIcon sx={{ fontSize: 14 }} />{customer.customerEmail}
                  </Typography>
                )}
              </Box>
              {customer?.customerSource && (
                <Chip label={`Source: ${customer.customerSource}`} size="small" variant="outlined" sx={{ mt: 0.8, fontSize: "0.7rem" }} />
              )}
            </Box>

            {/* Header Actions */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenLeadDialog(true)}
                sx={{ bgcolor: "#1976d2", textTransform: "none", borderRadius: 1.5 }}
              >
                New Lead
              </Button>
              <ContactActionButtons
                phone={customer?.customerMobileNum}
                email={customer?.customerEmail}
                name={fullName}
                /* customerId + leadId stamp every email sent from this
                   surface with CRM linkage. activeLead?.id captures the
                   currently-open inquiry so the email also threads into
                   the Lead View history; missing active lead is fine —
                   the email still lands in Client Profile history via
                   customer_id. onSent triggers a manual refetch since
                   ContactActionButtons can't see our RTK tag scope. */
                customerId={customerId ? Number(customerId) : null}
                leadId={activeLead?.id ?? null}
                onSent={() => refetchEmailHistory()}
                size="small"
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<NoteAddIcon />}
                onClick={() => { setNoteText(""); setNoteOpen(true); }}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                Add Note
              </Button>
              <Tooltip title="Edit Client">
                <IconButton size="small" onClick={() => setOpenEditCustomer(true)} sx={{ border: "1px solid #ddd" }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── KPI ROW ────────────────────────────────────────────────── */}
      {summaryLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
      ) : summary && (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
          <KpiCard label="Total Leads"     value={summary.total_leads}     icon={<PersonSearchIcon  sx={{ fontSize: 18, color: "#1976d2" }} />} color="#1976d2" />
          <KpiCard label="Active Leads"    value={summary.active_leads}    icon={<TrendingUpIcon    sx={{ fontSize: 18, color: "#0288d1" }} />} color="#0288d1" />
          <KpiCard label="Converted"       value={summary.converted_leads} icon={<CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#388e3c" }} />} color="#388e3c" />
          <KpiCard label="Lost"            value={summary.lost_leads}      icon={<CancelOutlinedIcon sx={{ fontSize: 18, color: "#e53935" }} />} color="#e53935" />
          <KpiCard label="Total Quotes"    value={summary.total_quotes}    icon={<DescriptionIcon   sx={{ fontSize: 18, color: "#7b1fa2" }} />} color="#7b1fa2" />
          <KpiCard label="Total Invoices"  value={summary.total_invoices}  icon={<ReceiptLongIcon   sx={{ fontSize: 18, color: "#f57c00" }} />} color="#f57c00" />
          <KpiCard label="Revenue"         value={formatCurrency(summary.total_revenue)}   icon={<AttachMoneyIcon sx={{ fontSize: 18, color: "#00796b" }} />} color="#00796b" />
          <KpiCard label="Avg Deal"        value={formatCurrency(summary.avg_deal_value)}  icon={<ShowChartIcon   sx={{ fontSize: 18, color: "#5d4037" }} />} color="#5d4037" sub={`${summary.conversion_rate}% conversion`} />
        </Box>
      )}

      {/* ── MAIN 2-COLUMN LAYOUT ────────────────────────────────────── */}
      <Grid container spacing={2.5}>

        {/* ╔═══════════════════════════════════╗
            ║  LEFT COLUMN — DATA               ║
            ╚═══════════════════════════════════╝ */}
        <Grid item xs={12} md={8}>

          {/* ── LEADS TABLE ──────────────────── */}
          <Card sx={{ mb: 2.5, border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: 2 }}>
            <CardContent sx={{ p: "16px !important" }}>
              <SectionHeader
                title="Leads"
                action={
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<PersonAddIcon />}
                      onClick={() => setOpenLeadDialog(true)}
                      sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}>
                      New Lead
                    </Button>
                    <Button size="small" variant="text"
                      onClick={() => navigate("/leads/pipeline")}
                      sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}>
                      View Pipeline
                    </Button>
                  </Box>
                }
              />
              {leadsLoading ? (
                <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
              ) : leads.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "#bbb" }}>
                  <PersonSearchIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2">No leads yet</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ borderRadius: 1.5, border: "1px solid #f0f0f0" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        {["Event Type","Event Date","Guests","Budget","Status","AI Score","Source","Assigned","Created",""].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#555", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow
                          key={lead.id}
                          hover
                          sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f0f7ff" } }}
                          onClick={() => navigate(`/leads/${lead.id}`)}
                        >
                          <TableCell sx={{ fontSize: "0.8rem" }}>{lead.event_type ?? "—"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatDate(lead.event_date)}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{lead.guest_count ?? "—"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{formatLeadBudget(lead)}</TableCell>
                          <TableCell>{leadStatusBadge(lead.status)}</TableCell>
                          <TableCell>{aiScoreBadge(lead.ai_score)}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{lead.source ?? "—"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{lead.assigned_to ?? "—"}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap", color: "#999" }}>{formatDateTime(lead.created_at)}</TableCell>
                          <TableCell>
                            <Tooltip title="View Lead">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }}>
                                <OpenInNewIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* ── QUOTES TABLE ─────────────────── */}
          <Card sx={{ mb: 2.5, border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: 2 }}>
            <CardContent sx={{ p: "16px !important" }}>
              <SectionHeader
                title="Quotes"
                action={
                  <Button size="small" variant="outlined" startIcon={<DescriptionIcon />}
                    onClick={() => navigate("/quote/list")}
                    sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}>
                    + Create Quote
                  </Button>
                }
              />
              {quotesLoading ? (
                <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
              ) : quotes.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "#bbb" }}>
                  <DescriptionIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2">No quotes yet</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ borderRadius: 1.5, border: "1px solid #f0f0f0" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        {["Quote #","Event Type","Event Date","Amount","Status","Created",""].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#555", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quotes.map((quote) => (
                        <TableRow
                          key={quote.id}
                          hover
                          sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f9f0ff" } }}
                          onClick={() => navigate(`/leads/${quote.lead_id}`)}
                        >
                          <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#7b1fa2" }}>{quote.quote_ref}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{quote.event_type ?? "—"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatDate(quote.event_date)}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{formatCurrency(quote.grand_total)}</TableCell>
                          <TableCell>{quoteStatusBadge(quote.status)}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem", color: "#999", whiteSpace: "nowrap" }}>{formatDate(quote.created_at)}</TableCell>
                          <TableCell>
                            <Tooltip title="View Quote">
                              <IconButton size="small">
                                <OpenInNewIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* ── INVOICES TABLE ───────────────── */}
          <Card sx={{ border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: 2 }}>
            <CardContent sx={{ p: "16px !important" }}>
              <SectionHeader
                title="Invoices"
                action={
                  <Button size="small" variant="outlined" startIcon={<ReceiptIcon />}
                    onClick={() => navigate("/invoice/list")}
                    sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}>
                    + Create Invoice
                  </Button>
                }
              />
              {invoicesLoading ? (
                <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
              ) : invoices.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "#bbb" }}>
                  <ReceiptLongIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2">No invoices yet</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ borderRadius: 1.5, border: "1px solid #f0f0f0" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        {["Invoice #","Amount","Paid","Due","Payment","Due Date","Created",""].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#555", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow
                          key={inv.id}
                          hover
                          sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f0fff4" } }}
                          onClick={() => navigate(`/invoice/list`)}
                        >
                          <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#388e3c" }}>#{inv.invoice_no}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{formatCurrency(inv.amount)}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#388e3c" }}>{formatCurrency(inv.paid_amount)}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: inv.due_amount && inv.due_amount > 0 ? "#e53935" : "#999" }}>
                            {formatCurrency(inv.due_amount)}
                          </TableCell>
                          <TableCell>{invoicePaymentBadge(inv)}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatDate(inv.due_date)}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem", color: "#999", whiteSpace: "nowrap" }}>{formatDate(inv.created_at)}</TableCell>
                          <TableCell>
                            <Tooltip title="View Invoice">
                              <IconButton size="small">
                                <OpenInNewIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ╔═══════════════════════════════════╗
            ║  RIGHT COLUMN — INSIGHTS          ║
            ╚═══════════════════════════════════╝ */}
        <Grid item xs={12} md={4}>

          {/* ── ACTIVE LEAD CARD ─────────────── */}
          {activeLead && (
            <InsightCard title="Active Lead">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {leadStatusBadge(activeLead.status)}
                {aiScoreBadge(activeLead.ai_score)}
              </Box>
              <InsightRow label="Event Type" value={activeLead.event_type ?? "—"} />
              <InsightRow label="Event Date"  value={formatDate(activeLead.event_date)} />
              <InsightRow label="Guests"      value={activeLead.guest_count ?? "—"} />
              <InsightRow label="Budget"      value={formatLeadBudget(activeLead)} />
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                <Button size="small" variant="contained"
                  onClick={() => navigate(`/leads/${activeLead.id}`)}
                  sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem", flex: 1 }}>
                  View Lead
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => navigate(`/leads/${activeLead.id}`)}
                  sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem", flex: 1 }}>
                  Update
                </Button>
              </Box>
            </InsightCard>
          )}

          {/* ── CLIENT VALUE CARD ────────────── */}
          <InsightCard title="Client Value">
            <InsightRow label="Total Revenue"   value={formatCurrency(summary?.total_revenue)}  />
            <InsightRow label="Avg Deal Size"   value={formatCurrency(summary?.avg_deal_value)} />
            <InsightRow label="Conversion Rate" value={`${summary?.conversion_rate ?? 0}%`}     />
            <InsightRow label="Total Invoices"  value={summary?.total_invoices ?? 0}             />
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="caption" sx={{ color: "#999" }}>Lifetime value</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#00796b" }}>
                {formatCurrency(summary?.total_revenue)}
              </Typography>
            </Box>
          </InsightCard>

          {/* ── CLIENT PREFERENCES & NOTES ───── */}
          <InsightCard title="Client Preferences & Notes">
            <PreferenceRow
              icon={<AccessTimeIcon sx={{ fontSize: 18, color: "#1976d2" }} />}
              iconColor="#1976d2"
              label="Preferred Time to Call"
              value={customer?.customerPreferTimeToCall}
            />
            <Divider sx={{ my: 1.5 }} />
            <PreferenceRow
              icon={<NotesIcon sx={{ fontSize: 18, color: "#0288d1" }} />}
              iconColor="#0288d1"
              label="Notes"
              value={customer?.customerDescription}
            />
          </InsightCard>

          {/* ── LEAD INSIGHTS ────────────────── */}
          {leads.length > 0 && (
            <InsightCard title="Lead Insights">
              <InsightRow label="Top event type"  value={topEventType}               />
              <InsightRow label="Avg guest count" value={avgGuests > 0 ? avgGuests : "—"} />
              <InsightRow label="Avg budget"      value={avgBudget > 0 ? formatCurrency(avgBudget) : "—"} />
              <InsightRow label="Preferred hall"  value={topHall}                    />
            </InsightCard>
          )}

          {/* ── JOURNEY INDICATOR ────────────── */}
          <InsightCard title="Journey">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              {[
                { label: "Leads",    count: summary?.total_leads ?? 0,    color: "#1976d2", active: (summary?.total_leads ?? 0) > 0 },
                { label: "Quotes",   count: summary?.total_quotes ?? 0,   color: "#7b1fa2", active: (summary?.total_quotes ?? 0) > 0 },
                { label: "Invoices", count: summary?.total_invoices ?? 0, color: "#388e3c", active: (summary?.total_invoices ?? 0) > 0 },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  <Box sx={{
                    px: 1.5, py: 0.5, borderRadius: 2,
                    bgcolor: step.active ? `${step.color}18` : "#f5f5f5",
                    border: `1px solid ${step.active ? step.color : "#e0e0e0"}`,
                    textAlign: "center", flex: 1,
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: step.active ? step.color : "#bbb", display: "block" }}>
                      {step.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: step.active ? step.color : "#ccc", lineHeight: 1 }}>
                      {step.count}
                    </Typography>
                  </Box>
                  {i < 2 && <ArrowForwardIosIcon sx={{ fontSize: 12, color: "#bbb" }} />}
                </React.Fragment>
              ))}
            </Box>
          </InsightCard>

          {/* ── ACTIVITY TIMELINE ────────────── */}
          <InsightCard title="Activity Timeline">
            {timelineLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={20} /></Box>
            ) : timeline.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#bbb", textAlign: "center", py: 2 }}>No activity yet</Typography>
            ) : (
              <Box>
                {timeline.slice(0, 15).map((event, idx) => (
                  <Box key={event.id} sx={{ display: "flex", gap: 1.5, mb: idx < timeline.length - 1 ? 1.5 : 0 }}>
                    {/* Icon column */}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <Box sx={{ bgcolor: "#f5f5f5", borderRadius: "50%", p: 0.6, display: "flex" }}>
                        <TimelineIcon type={event.type} />
                      </Box>
                      {idx < timeline.slice(0, 15).length - 1 && (
                        <Box sx={{ width: 1.5, flex: 1, bgcolor: "#eee", mt: 0.5 }} />
                      )}
                    </Box>
                    {/* Text */}
                    <Box sx={{ flex: 1, pb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "#333", lineHeight: 1.3 }}>
                        {event.description}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#aaa", fontSize: "0.7rem" }}>
                        {formatDateTime(event.timestamp)}
                        {event.staff ? ` · ${event.staff}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </InsightCard>

          {/* ── INTERNAL NOTES ────────────────
              Backed by /customers/:id/notes — RTK-tagged so the Add
              Note mutation invalidates this query and the list
              refreshes the moment a note is saved. */}
          <InsightCard title="Internal Notes">
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<NoteAddIcon sx={{ fontSize: 16 }} />}
                onClick={() => { setNoteText(""); setNoteOpen(true); }}
                sx={{ textTransform: "none", fontSize: "0.72rem" }}
              >
                Add Note
              </Button>
            </Box>
            {(() => {
              const notes = notesData?.data ?? [];
              if (notes.length === 0) {
                return (
                  <Typography variant="body2" sx={{ color: "#bbb", textAlign: "center", py: 2, fontSize: "0.8rem" }}>
                    No internal notes yet
                  </Typography>
                );
              }
              return (
                <Box>
                  {notes.slice(0, 10).map((n) => (
                    <Box key={n.id} sx={{ mb: 1.2, pb: 1.2, borderBottom: "1px dashed #eee", "&:last-child": { borderBottom: "none", pb: 0, mb: 0 } }}>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                        {n.note}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem", display: "block", mt: 0.3 }}>
                        {n.user?.name ?? "—"} · {formatDateTime(n.created_at)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </InsightCard>

          {/* ── EMAIL SEND HISTORY ────────────
              Audit list of every outbound email attributed to this
              client. Each row carries status + sent-by + template +
              recipient; clicking "View" pops the full body modal. */}
          <InsightCard title="Email Sent History">
            {emailHistoryLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={20} /></Box>
            ) : (emailHistoryData?.data?.length ?? 0) === 0 ? (
              <Typography variant="body2" sx={{ color: "#bbb", textAlign: "center", py: 2, fontSize: "0.8rem" }}>
                No emails sent yet
              </Typography>
            ) : (
              <Box>
                {(emailHistoryData?.data ?? []).slice(0, 12).map((row) => {
                  const failed = row.status === "failed";
                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: "flex", gap: 1.2,
                        py: 1, borderBottom: "1px dashed #eee",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{
                        bgcolor: failed ? "#fdecea" : "#ede7f6",
                        borderRadius: "50%", p: 0.6, display: "flex", flexShrink: 0,
                        height: "fit-content",
                      }}>
                        <EmailIcon sx={{ fontSize: 16, color: failed ? "#c62828" : "#9c27b0" }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#333" }}>
                            {row.subject || "(no subject)"}
                          </Typography>
                          <Chip
                            label={failed ? "Failed" : "Sent"}
                            size="small"
                            sx={{
                              height: 16, fontSize: 9, fontWeight: 700,
                              bgcolor: failed ? "#fdecea" : "#e8f5e9",
                              color: failed ? "#c62828" : "#2e7d32",
                            }}
                          />
                          {row.template?.name && (
                            <Chip
                              label={row.template.name}
                              size="small"
                              sx={{ height: 16, fontSize: 9, fontWeight: 600, bgcolor: "#fff3e0", color: "#e65100" }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: "#666", fontSize: "0.7rem", display: "block", mt: 0.2 }}>
                          To: {row.recipient || "—"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem", display: "block" }}>
                          {row.sent_by?.name ?? "—"} · {formatDateTime(row.sent_at ?? null)}
                        </Typography>
                        {failed && row.error_message && (
                          <Typography variant="caption" sx={{ color: "#c62828", fontSize: "0.7rem", display: "block", mt: 0.3 }}>
                            {row.error_message}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setViewEmail(row)}
                          sx={{ textTransform: "none", fontSize: "0.7rem", px: 0.5, mt: 0.3, minWidth: 0 }}
                        >
                          View Message
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </InsightCard>
        </Grid>
      </Grid>

      {/* ── EDIT CUSTOMER DIALOG ────────────────────────────────────── */}
      {openEditCustomer && customer && (
        <NewPopUpCustomer
          openModel={openEditCustomer}
          handleCloseDialog={() => setOpenEditCustomer(false)}
          data={customer}
        />
      )}

      <CreateLeadDialog
        open={openLeadDialog}
        onClose={() => setOpenLeadDialog(false)}
        onCreated={() => {
          setOpenLeadDialog(false);
          // Explicit refetch in addition to the leadBus subscription —
          // if the bus event hasn't propagated by the time the modal
          // closes (RTK Query is asynchronous), the operator still sees
          // their new lead appear immediately.
          refreshCustomerLeadFeeds();
        }}
        defaultCustomer={customer}
      />

      {/* ── ADD NOTE DIALOG ─────────────────────────────────────────
          Closes on ESC, click-outside (unless saving), Cancel, and a
          successful Save. ⌘/Ctrl+Enter is a save shortcut so the
          rep doesn't have to mouse-hop after typing. */}
      <Dialog
        open={noteOpen}
        onClose={() => !noteSaving && setNoteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>
          Add Internal Note
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Visible only to your team. Saved to this client's notes &amp; history.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="What would you like to record about this client?"
            disabled={noteSaving}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleSaveNote();
              }
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 14 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoteOpen(false)} disabled={noteSaving} sx={{ textTransform: "none", color: "#666" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveNote}
            disabled={noteSaving || !noteText.trim()}
            variant="contained"
            disableElevation
            startIcon={noteSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NoteAddIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#1976d2" }}
          >
            {noteSaving ? "Saving…" : "Save Note"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VIEW SENT MESSAGE DIALOG ─────────────────────────────────
          Renders the raw body_html in a sandboxed scrolling container
          so existing template HTML (lists, tables, inline styles)
          displays the way the customer saw it. */}
      <Dialog
        open={!!viewEmail}
        onClose={() => setViewEmail(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>
          {viewEmail?.subject || "(no subject)"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {viewEmail && (
            <>
              <Box sx={{ mb: 2, color: "#555", fontSize: "0.8rem", display: "flex", flexDirection: "column", gap: 0.3 }}>
                <div><b>To:</b> {viewEmail.recipient}</div>
                <div><b>Sent by:</b> {viewEmail.sent_by?.name ?? "—"}</div>
                <div><b>Sent at:</b> {formatDateTime(viewEmail.sent_at ?? null)}</div>
                <div>
                  <b>Status:</b>{" "}
                  <Chip
                    label={viewEmail.status === "failed" ? "Failed" : "Sent"}
                    size="small"
                    sx={{
                      height: 18, fontSize: 10, fontWeight: 700,
                      bgcolor: viewEmail.status === "failed" ? "#fdecea" : "#e8f5e9",
                      color:   viewEmail.status === "failed" ? "#c62828" : "#2e7d32",
                    }}
                  />
                </div>
                {viewEmail.template?.name && <div><b>Template:</b> {viewEmail.template.name}</div>}
                {viewEmail.status === "failed" && viewEmail.error_message && (
                  <div style={{ color: "#c62828" }}><b>Error:</b> {viewEmail.error_message}</div>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* The body is HTML produced by the same composer used in
                  Email Hub / Lead View — rendering it raw preserves
                  formatting + the tracking pixel (harmless here). */}
              <Box
                sx={{
                  "& img": { maxWidth: "100%" },
                  "& a":   { color: "#1976d2", textDecoration: "underline" },
                  fontSize: "0.9rem",
                  color: "#222",
                  lineHeight: 1.5,
                  maxHeight: "55vh",
                  overflowY: "auto",
                }}
                dangerouslySetInnerHTML={{ __html: viewEmail.body_html || viewEmail.body_preview || "" }}
              />
              {viewEmail.attachments && viewEmail.attachments.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", mb: 1 }}>
                    Attachments ({viewEmail.attachments.length})
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {viewEmail.attachments.map((att) => (
                      <Chip
                        key={att.id}
                        icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                        label={`${att.file_name}${att.file_size ? ` (${Math.round(att.file_size / 1024)}KB)` : ""}`}
                        size="small"
                        clickable={!!att.file_url}
                        onClick={att.file_url ? () => window.open(`${API_URL}/../${att.file_url}`, "_blank") : undefined}
                        sx={{ fontSize: 11, height: 26, bgcolor: "#f4f2ee", "&:hover": att.file_url ? { bgcolor: "#f5eed9" } : {} }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewEmail(null)} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ─────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerCRMProfile;
